/**
 * Lightweight SQL-like query parser and executor for dashboard queries
 *
 * Supports basic SQL operations:
 * - SELECT: field selection with *, specific fields, and aggregate functions
 * - FROM: entity selection (commits, prs, issues, releases)
 * - WHERE: filtering with AND/OR conditions
 * - GROUP BY: grouping by fields
 * - ORDER BY: sorting results
 * - LIMIT: limiting result count
 */

type EntityType = "commits" | "prs" | "issues" | "releases"

type AggregateFunction = "COUNT" | "SUM" | "AVG" | "MIN" | "MAX"

interface SelectField {
  field: string
  alias?: string
  aggregate?: AggregateFunction
}

interface WhereCondition {
  field: string
  operator: "=" | "!=" | ">" | "<" | ">=" | "<=" | "LIKE" | "IN"
  value: string | number | boolean | string[]
  combiner?: "AND" | "OR"
}

interface OrderByClause {
  field: string
  direction: "ASC" | "DESC"
}

export interface ParsedQuery {
  select: SelectField[]
  from: EntityType
  where: WhereCondition[]
  groupBy: string[]
  orderBy: OrderByClause[]
  limit?: number
}

export interface QueryResult {
  columns: string[]
  rows: Record<string, unknown>[]
  totalRows: number
}

/**
 * Entity schemas for autocomplete and validation
 */
export const ENTITY_SCHEMAS = {
  commits: {
    fields: [
      "hash",
      "repoName",
      "authorHandle",
      "timestamp",
      "score",
      "complexityClass",
      "filesChanged",
      "insertions",
      "deletions",
      "functionsChanged",
      "cyclomaticDelta",
      "maxNestingDelta",
      "commitType",
      "message",
    ],
    types: {
      hash: "string",
      repoName: "string",
      authorHandle: "string",
      timestamp: "number",
      score: "number",
      complexityClass: "string",
      filesChanged: "number",
      insertions: "number",
      deletions: "number",
      functionsChanged: "number",
      cyclomaticDelta: "number",
      maxNestingDelta: "number",
      commitType: "string",
      message: "string",
    },
  },
  prs: {
    fields: [
      "id",
      "repoName",
      "number",
      "title",
      "creatorHandle",
      "createdAt",
      "mergedAt",
      "closedAt",
      "state",
      "draft",
      "additions",
      "deletions",
      "changedFiles",
      "comments",
    ],
    types: {
      id: "number",
      repoName: "string",
      number: "number",
      title: "string",
      creatorHandle: "string",
      createdAt: "number",
      mergedAt: "number",
      closedAt: "number",
      state: "string",
      draft: "boolean",
      additions: "number",
      deletions: "number",
      changedFiles: "number",
      comments: "number",
    },
  },
  issues: {
    fields: [
      "id",
      "repoName",
      "number",
      "title",
      "creatorHandle",
      "createdAt",
      "closedAt",
      "closedByHandle",
      "state",
      "comments",
      "commentAuthors",
      "firstCommentAt",
      "lastCommentAt",
    ],
    types: {
      id: "number",
      repoName: "string",
      number: "number",
      title: "string",
      creatorHandle: "string",
      createdAt: "number",
      closedAt: "number",
      closedByHandle: "string",
      state: "string",
      comments: "number",
      commentAuthors: "number",
      firstCommentAt: "number",
      lastCommentAt: "number",
    },
  },
  releases: {
    fields: [
      "id",
      "repoName",
      "tagName",
      "name",
      "authorHandle",
      "createdAt",
      "publishedAt",
      "draft",
      "prerelease",
    ],
    types: {
      id: "number",
      repoName: "string",
      tagName: "string",
      name: "string",
      authorHandle: "string",
      createdAt: "number",
      publishedAt: "number",
      draft: "boolean",
      prerelease: "boolean",
    },
  },
} as const

const SQL_KEYWORDS = [
  "SELECT",
  "FROM",
  "WHERE",
  "GROUP",
  "BY",
  "ORDER",
  "LIMIT",
  "AND",
  "OR",
  "AS",
  "COUNT",
  "SUM",
  "AVG",
  "MIN",
  "MAX",
  "LIKE",
  "IN",
]

/**
 * Parse SQL-like query string into structured query object
 */
export function parseQuery(queryString: string): ParsedQuery {
  const query = queryString.trim()

  // Extract FROM clause to get entity
  const fromMatch = query.match(/FROM\s+(\w+)/i)
  if (!fromMatch) {
    throw new Error("FROM clause is required")
  }
  const from = fromMatch[1].toLowerCase() as EntityType

  if (!ENTITY_SCHEMAS[from]) {
    throw new Error(`Unknown entity: ${from}. Available entities: commits, prs, issues, releases`)
  }

  // Extract SELECT clause
  const selectMatch = query.match(/SELECT\s+(.*?)\s+FROM/i)
  if (!selectMatch) {
    throw new Error("SELECT clause is required")
  }
  const selectStr = selectMatch[1].trim()
  const select = parseSelectFields(selectStr, from)

  // Extract WHERE clause
  const where: WhereCondition[] = []
  const whereMatch = query.match(/WHERE\s+(.*?)(?:GROUP BY|ORDER BY|LIMIT|$)/i)
  if (whereMatch) {
    const whereStr = whereMatch[1].trim()
    where.push(...parseWhereConditions(whereStr))
  }

  // Extract GROUP BY clause
  const groupBy: string[] = []
  const groupByMatch = query.match(/GROUP BY\s+(.*?)(?:ORDER BY|LIMIT|$)/i)
  if (groupByMatch) {
    groupBy.push(
      ...groupByMatch[1]
        .split(",")
        .map((f) => f.trim())
        .filter(Boolean),
    )
  }

  // Extract ORDER BY clause
  const orderBy: OrderByClause[] = []
  const orderByMatch = query.match(/ORDER BY\s+(.*?)(?:LIMIT|$)/i)
  if (orderByMatch) {
    const orderByStr = orderByMatch[1].trim()
    orderBy.push(...parseOrderBy(orderByStr))
  }

  // Extract LIMIT clause
  let limit: number | undefined
  const limitMatch = query.match(/LIMIT\s+(\d+)/i)
  if (limitMatch) {
    limit = Number.parseInt(limitMatch[1], 10)
  }

  return {
    select,
    from,
    where,
    groupBy,
    orderBy,
    limit,
  }
}

/**
 * Parse SELECT fields (supports *, field names, aggregates, aliases)
 */
function parseSelectFields(selectStr: string, entity: EntityType): SelectField[] {
  if (selectStr === "*") {
    return ENTITY_SCHEMAS[entity].fields.map((field) => ({ field }))
  }

  const fields: SelectField[] = []
  const parts = selectStr.split(",").map((p) => p.trim())

  for (const part of parts) {
    // Check for aggregate functions: COUNT(*), SUM(field), etc.
    const aggMatch = part.match(/^(COUNT|SUM|AVG|MIN|MAX)\((.*?)\)(?:\s+AS\s+(\w+))?$/i)
    if (aggMatch) {
      const [, agg, field, alias] = aggMatch
      fields.push({
        field: field.trim(),
        aggregate: agg.toUpperCase() as AggregateFunction,
        alias: alias || `${agg.toLowerCase()}_${field === "*" ? "all" : field}`,
      })
      continue
    }

    // Check for alias: field AS alias
    const aliasMatch = part.match(/^(\w+)\s+AS\s+(\w+)$/i)
    if (aliasMatch) {
      fields.push({
        field: aliasMatch[1],
        alias: aliasMatch[2],
      })
      continue
    }

    // Simple field
    fields.push({ field: part })
  }

  return fields
}

/**
 * Parse WHERE conditions
 */
function parseWhereConditions(whereStr: string): WhereCondition[] {
  const conditions: WhereCondition[] = []

  // Split by AND/OR while preserving the combiner
  const parts = whereStr.split(/\s+(AND|OR)\s+/i)

  for (let i = 0; i < parts.length; i += 2) {
    const condStr = parts[i].trim()
    const combiner = parts[i + 1] as "AND" | "OR" | undefined

    // Parse single condition: field operator value
    const match = condStr.match(/^(\w+)\s*(=|!=|>|<|>=|<=|LIKE|IN)\s*(.+)$/i)
    if (!match) {
      throw new Error(`Invalid WHERE condition: ${condStr}`)
    }

    const [, field, operator, valueStr] = match
    let value: string | number | boolean | string[]

    // Handle IN operator with array values
    if (operator.toUpperCase() === "IN") {
      const inMatch = valueStr.match(/^\((.*)\)$/)
      if (!inMatch) {
        throw new Error(`IN operator requires parentheses: ${valueStr}`)
      }
      value = inMatch[1].split(",").map((v) => v.trim().replace(/^['"]|['"]$/g, ""))
    } else {
      // Parse single value
      value = parseValue(valueStr)
    }

    conditions.push({
      field,
      operator: operator as WhereCondition["operator"],
      value,
      combiner,
    })
  }

  return conditions
}

/**
 * Parse ORDER BY clauses
 */
function parseOrderBy(orderByStr: string): OrderByClause[] {
  return orderByStr.split(",").map((clause) => {
    const parts = clause.trim().split(/\s+/)
    return {
      field: parts[0],
      direction: (parts[1]?.toUpperCase() || "ASC") as "ASC" | "DESC",
    }
  })
}

/**
 * Parse value based on type
 */
function parseValue(valueStr: string): string | number | boolean {
  const trimmed = valueStr.trim()

  // String literal
  if (trimmed.startsWith("'") || trimmed.startsWith('"')) {
    return trimmed.slice(1, -1)
  }

  // Boolean
  if (trimmed.toLowerCase() === "true") return true
  if (trimmed.toLowerCase() === "false") return false

  // Number
  if (!Number.isNaN(Number(trimmed))) {
    return Number(trimmed)
  }

  return trimmed
}

/**
 * Execute parsed query against data
 */
export function executeQuery(query: ParsedQuery, data: Record<string, unknown>[]): QueryResult {
  let results = [...data]

  // Apply WHERE filters
  if (query.where.length > 0) {
    results = results.filter((row) => evaluateWhereConditions(row, query.where))
  }

  // Apply GROUP BY and aggregates
  if (query.groupBy.length > 0 || query.select.some((s) => s.aggregate)) {
    results = applyGroupBy(results, query)
  } else {
    // Apply simple SELECT projection
    results = results.map((row) => projectRow(row, query.select))
  }

  // Apply ORDER BY
  if (query.orderBy.length > 0) {
    results.sort((a, b) => {
      for (const order of query.orderBy) {
        const aVal = a[order.field] as number | string
        const bVal = b[order.field] as number | string

        let cmp = 0
        if (aVal < bVal) cmp = -1
        else if (aVal > bVal) cmp = 1

        if (cmp !== 0) {
          return order.direction === "ASC" ? cmp : -cmp
        }
      }
      return 0
    })
  }

  const totalRows = results.length

  // Apply LIMIT
  if (query.limit) {
    results = results.slice(0, query.limit)
  }

  // Extract column names
  const columns = results.length > 0 ? Object.keys(results[0]) : []

  return {
    columns,
    rows: results,
    totalRows,
  }
}

/**
 * Evaluate WHERE conditions for a row
 */
function evaluateWhereConditions(
  row: Record<string, unknown>,
  conditions: WhereCondition[],
): boolean {
  if (conditions.length === 0) return true

  let result = evaluateCondition(row, conditions[0])

  for (let i = 1; i < conditions.length; i++) {
    const prevCombiner = conditions[i - 1].combiner
    const condResult = evaluateCondition(row, conditions[i])

    if (prevCombiner === "AND") {
      result = result && condResult
    } else if (prevCombiner === "OR") {
      result = result || condResult
    }
  }

  return result
}

/**
 * Evaluate single condition
 */
function evaluateCondition(row: Record<string, unknown>, condition: WhereCondition): boolean {
  const rowValue = row[condition.field]
  const { operator, value } = condition

  switch (operator) {
    case "=":
      return rowValue === value
    case "!=":
      return rowValue !== value
    case ">":
      return (rowValue as number) > (value as number)
    case "<":
      return (rowValue as number) < (value as number)
    case ">=":
      return (rowValue as number) >= (value as number)
    case "<=":
      return (rowValue as number) <= (value as number)
    case "LIKE": {
      const pattern = (value as string).replace(/%/g, ".*")
      return new RegExp(pattern, "i").test(String(rowValue))
    }
    case "IN":
      return (value as string[]).includes(String(rowValue))
    default:
      return false
  }
}

/**
 * Project row to selected fields
 */
function projectRow(
  row: Record<string, unknown>,
  selectFields: SelectField[],
): Record<string, unknown> {
  const result: Record<string, unknown> = {}

  for (const field of selectFields) {
    const key = field.alias || field.field
    result[key] = row[field.field]
  }

  return result
}

/**
 * Apply GROUP BY and aggregate functions
 */
function applyGroupBy(
  data: Record<string, unknown>[],
  query: ParsedQuery,
): Record<string, unknown>[] {
  const { groupBy, select } = query

  if (groupBy.length === 0) {
    // No grouping, just aggregates over all rows
    const result: Record<string, unknown> = {}

    for (const field of select) {
      if (field.aggregate) {
        result[field.alias || field.field] = calculateAggregate(data, field.field, field.aggregate)
      }
    }

    return [result]
  }

  // Group by specified fields
  const groups = new Map<string, Record<string, unknown>[]>()

  for (const row of data) {
    const groupKey = groupBy.map((field) => row[field]).join("|")

    if (!groups.has(groupKey)) {
      groups.set(groupKey, [])
    }
    groups.get(groupKey)?.push(row)
  }

  // Calculate aggregates for each group
  const results: Record<string, unknown>[] = []

  for (const [, groupRows] of groups) {
    const result: Record<string, unknown> = {}

    // Add group by fields
    for (let i = 0; i < groupBy.length; i++) {
      result[groupBy[i]] = groupRows[0][groupBy[i]]
    }

    // Add aggregates
    for (const field of select) {
      if (field.aggregate) {
        result[field.alias || field.field] = calculateAggregate(
          groupRows,
          field.field,
          field.aggregate,
        )
      } else if (!groupBy.includes(field.field)) {
        // Non-grouped, non-aggregate field - take first value
        result[field.alias || field.field] = groupRows[0][field.field]
      }
    }

    results.push(result)
  }

  return results
}

/**
 * Calculate aggregate function
 */
function calculateAggregate(
  data: Record<string, unknown>[],
  field: string,
  aggregate: AggregateFunction,
): number {
  switch (aggregate) {
    case "COUNT":
      return field === "*" ? data.length : data.filter((row) => row[field] != null).length
    case "SUM":
      return data.reduce((sum, row) => sum + (Number(row[field]) || 0), 0)
    case "AVG": {
      const values = data.filter((row) => row[field] != null)
      const sum = values.reduce((s, row) => s + Number(row[field]), 0)
      return values.length > 0 ? sum / values.length : 0
    }
    case "MIN": {
      const values = data.map((row) => Number(row[field])).filter((v) => !Number.isNaN(v))
      return values.length > 0 ? Math.min(...values) : 0
    }
    case "MAX": {
      const values = data.map((row) => Number(row[field])).filter((v) => !Number.isNaN(v))
      return values.length > 0 ? Math.max(...values) : 0
    }
    default:
      return 0
  }
}

/**
 * Get autocomplete suggestions for query at cursor position
 */
export function getAutocompleteSuggestions(query: string, cursorPosition: number): string[] {
  const beforeCursor = query.substring(0, cursorPosition)
  const lastWord = beforeCursor.split(/\s+/).pop() || ""

  // Check context to provide relevant suggestions
  const upperQuery = beforeCursor.toUpperCase()

  // After SELECT, suggest fields or aggregates
  if (upperQuery.includes("SELECT") && !upperQuery.includes("FROM")) {
    const suggestions = ["*", ...Object.keys(ENTITY_SCHEMAS)]
    return [...SQL_KEYWORDS, ...suggestions].filter((s) =>
      s.toLowerCase().startsWith(lastWord.toLowerCase()),
    )
  }

  // After FROM, suggest entities
  if (upperQuery.includes("FROM") && !upperQuery.match(/FROM\s+\w+/)) {
    return Object.keys(ENTITY_SCHEMAS).filter((e) =>
      e.toLowerCase().startsWith(lastWord.toLowerCase()),
    )
  }

  // Try to detect which entity we're querying
  const fromMatch = upperQuery.match(/FROM\s+(\w+)/)
  if (fromMatch) {
    const entity = fromMatch[1].toLowerCase() as EntityType
    if (ENTITY_SCHEMAS[entity]) {
      const fields = ENTITY_SCHEMAS[entity].fields
      const fieldSuggestions = fields.filter((f) =>
        f.toLowerCase().startsWith(lastWord.toLowerCase()),
      )

      if (fieldSuggestions.length > 0) {
        return fieldSuggestions
      }
    }
  }

  // Default: suggest SQL keywords
  return SQL_KEYWORDS.filter((k) => k.toLowerCase().startsWith(lastWord.toLowerCase()))
}
